import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { AlarmClock } from 'lucide-react';
function getInitialTarget(): number {
  if (typeof window === 'undefined') return Date.now() + 24 * 60 * 60 * 1000;
  const stored = window.localStorage.getItem('mp_countdown_target');
  if (stored) {
    const parsed = parseInt(stored, 10);
    if (!isNaN(parsed) && parsed > Date.now()) return parsed;
  }
  const target = Date.now() + 24 * 60 * 60 * 1000;
  try {
    window.localStorage.setItem('mp_countdown_target', target.toString());
  } catch {}
  return target;
}
function pad(n: number) {
  return n.toString().padStart(2, '0');
}
export function CountdownTimer() {
  const [target, setTarget] = useState<number | null>(null);
  const [remaining, setRemaining] = useState(24 * 60 * 60 * 1000);
  useEffect(() => {
    const t = getInitialTarget();
    setTarget(t);
  }, []);
  useEffect(() => {
    if (!target) return;
    const tick = () => {
      const diff = target - Date.now();
      if (diff <= 0) {
        // restart another 24h cycle for urgency
        const next = Date.now() + 24 * 60 * 60 * 1000;
        try {
          window.localStorage.setItem('mp_countdown_target', next.toString());
        } catch {}
        setTarget(next);
        setRemaining(24 * 60 * 60 * 1000);
      } else {
        setRemaining(diff);
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [target]);
  const totalHours = Math.floor(remaining / (1000 * 60 * 60));
  const minutes = Math.floor(remaining / (1000 * 60) % 60);
  const seconds = Math.floor(remaining / 1000 % 60);
  // Simulated progress: starts around 65% and slowly increases toward 90% as time runs out
  const elapsed = 24 * 60 * 60 * 1000 - remaining;
  const progress = Math.min(90, 65 + elapsed / (24 * 60 * 60 * 1000) * 25);
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} className="mt-10 max-w-3xl mx-auto bg-gradient-to-b from-[#2a0808] to-[#1a0404] border border-red-500/40 rounded-2xl p-6 md:p-8 shadow-[0_0_30px_rgba(239,68,68,0.15)]" data-id="element-41" data-mp="2dnxusc7sry76">
      <div className="text-center" data-id="element-42" data-mp="2jaq76gvb5kkm">
        <div className="flex items-center justify-center gap-2 text-red-400 font-bold tracking-wider uppercase text-sm md:text-base mb-6" data-id="element-43" data-mp="2jdvxu0jl4uig">
          <AlarmClock size={20} className="animate-pulse" data-id="element-44" data-mp="29e52jwn7abhb" />
          <span data-id="element-45" data-mp="29fpzfswzm6ic">El precio de lanzamiento termina en:</span>
        </div>

        <div className="flex items-center justify-center gap-3 md:gap-5 mb-5" data-id="element-46" data-mp="24llmmt11j49c">
          <TimeBox value={pad(totalHours)} label="hs" data-id="element-47" data-mp="1zp3l0210bm18" />
          <span className="text-3xl md:text-5xl font-bold text-red-400" data-id="element-48" data-mp="1vmguc4g2o75m">:</span>
          <TimeBox value={pad(minutes)} label="min" data-id="element-49" data-mp="1vkduksp4aznp" />
          <span className="text-3xl md:text-5xl font-bold text-red-400" data-id="element-50" data-mp="1qmc8s00b4m8j">:</span>
          <TimeBox value={pad(seconds)} label="seg" data-id="element-51" data-mp="1lnr9c2svq6l5" />
        </div>

        <p className="text-xs md:text-sm font-bold tracking-wider uppercase text-gray-200 mb-8" data-id="element-52" data-mp="1lqn79r4q8v9l">
          Después sube el precio y no vuelve a bajar.
        </p>

        <div className="space-y-2" data-id="element-53" data-mp="641p86awgu6b">
          <div className="flex justify-between text-[11px] md:text-xs font-bold uppercase tracking-wider" data-id="element-54" data-mp="676540xq9ect">
            <span className="text-red-300" data-id="element-55" data-mp="f617t31kc6bd">Cupos al precio de lanzamiento</span>
            <span className="text-red-400" data-id="element-56" data-mp="f3x5uqn85hdq">Quedan pocos</span>
          </div>
          <div className="w-full h-2.5 bg-red-950/60 rounded-full overflow-hidden border border-red-500/30" data-id="element-57" data-mp="3emps4i7t20jf">
            <motion.div initial={{
            width: 0
          }} whileInView={{
            width: `${progress}%`
          }} viewport={{
            once: true
          }} transition={{
            duration: 1.5,
            ease: 'easeOut'
          }} className="h-full bg-gradient-to-r from-red-600 to-red-400 rounded-full shadow-[0_0_10px_rgba(239,68,68,0.6)]" data-id="element-58" data-mp="3jkhhp5kz9akg" />
          </div>
          <p className="text-right text-[11px] md:text-xs text-red-300 font-medium" data-id="element-59" data-mp="2gpj65w93sud9">
            {Math.round(progress)}% de los cupos tomados
          </p>
        </div>
      </div>
    </motion.div>;
}
function TimeBox({
  value,
  label
}: {
  value: string;
  label: string;
}) {
  return <div className="bg-black border-2 border-red-500/40 rounded-xl px-4 md:px-6 py-3 md:py-4 min-w-[80px] md:min-w-[110px] shadow-[inset_0_0_15px_rgba(239,68,68,0.1)]" data-id="element-60" data-mp="2lpf87py8qbq6">
      <div className="flex items-baseline justify-center gap-1" data-id="element-61" data-mp="2ui0dq4cv2lx2">
        <span className="text-4xl md:text-5xl font-bold text-white tabular-nums" data-id="element-62" data-mp="39gayujqyn0vu">
          {value}
        </span>
        <span className="text-xs md:text-sm font-medium text-red-300" data-id="element-63" data-mp="34goku83b5glz">
          {label}
        </span>
      </div>
    </div>;
}